import React, { useCallback, useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
	Title,
	Badge,
	Box,
	Group,
	Stack,
	Card,
	Button,
	Anchor,
	Table,
	Loader,
	Text,
	Divider,
	Tooltip,
	ActionIcon,
	Modal,
	SegmentedControl,
	Menu,
	TextInput,
	Progress,
	Pagination,
} from '@mantine/core'
import { AlertCircle } from 'tabler-icons-react'
import { saveAs } from 'file-saver'
import { buildBlastRecipientCsv } from '../services/utils/blastRecipientCsv'
import {
	getBlastScheduleError,
	toUtcIsoInTimezone,
} from '../services/utils/blastScheduling'
import {
	cancelScheduledBlast,
	deleteBlast,
	getBlast,
	getBlastStats,
	getBlastQueueHealth,
	listRecipients,
	listAllRecipients,
	sendBlast,
} from '../services/actions/messagingActions'
import { getExperienceCheckIns } from '../services/actions/checkInActions'
import BlastSendModal from '../components/BlastSendModal'
import {
	appendPassLinkToSmsBody,
	buildPassPreviewUrl,
	getStockTemplate,
	isCheckInPassTemplate,
} from '../constants/stockTemplates'

const replaceAll = (value, needle, replacement) =>
	String(value || '')
		.split(needle)
		.join(replacement)

const applyMessageVariables = ({
	value = '',
	travelerName = '',
	groupLeaderName = '',
	groupLeaderPhone = '',
	groupMemberNames = '',
	experienceName = '',
	orgName = '',
	clickHereToViewPass = '',
}) => {
	let output = String(value || '')
	output = replaceAll(output, '{{travelerName}}', travelerName || '')
	output = replaceAll(output, '{{groupLeaderName}}', groupLeaderName || '')
	output = replaceAll(output, '{{groupLeaderPhone}}', groupLeaderPhone || '')
	output = replaceAll(output, '{{groupMemberNames}}', groupMemberNames || '')
	output = replaceAll(output, '{{experienceName}}', experienceName || '')
	output = replaceAll(output, '{{orgName}}', orgName || '')
	output = replaceAll(
		output,
		'{{clickHereToViewPass}}',
		clickHereToViewPass || '',
	)
	return output
}

const getNonEmptyTrimmedValue = (...values) => {
	for (const value of values) {
		if (typeof value !== 'string') continue
		const normalized = value.trim()
		if (normalized) return normalized
	}
	return null
}

const getBrowserTimezone = () =>
	Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

const toDateTimeLocalInTimezone = (isoValue, timezone) => {
	if (!isoValue) return ''
	const date = new Date(isoValue)
	if (!Number.isFinite(date.getTime())) return ''
	const formatter = new Intl.DateTimeFormat('en-CA', {
		timeZone: timezone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		hourCycle: 'h23',
	})
	const parts = formatter
		.formatToParts(date)
		.reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {})
	return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
}

const toSafeCount = (value) => {
	const parsed = Number(value)
	if (!Number.isFinite(parsed)) return 0
	return Math.max(0, Math.floor(parsed))
}

const BLAST_IN_FLIGHT_POLL_INTERVAL_MS = 3000
const RECIPIENTS_PAGE_SIZE = 50
const RECIPIENT_STATUS_PILLS = [
	{ value: 'SENT', label: 'sent', color: 'blue' },
	{ value: 'DELIVERED', label: 'delivered', color: 'green' },
	{ value: 'OPENED', label: 'opened', color: 'grape' },
	// { value: 'CLICKED', label: 'clicked', color: 'yellow' },
	{ value: 'BOUNCED', label: 'bounced', color: 'orange' },
	{ value: 'UNSUBSCRIBED', label: 'unsubscribed', color: 'pink' },
	{ value: 'FAILED', label: 'failed', color: 'red' },
]

export default function BlastDetailPage() {
	const { orgSlug, experienceSlug, id } = useParams()
	const navigate = useNavigate()
	const experience = useSelector((state) => state.experience?.experienceDetails)
	const currentExperience = useSelector(
		(state) => state.user?.currentExperience,
	)
	const user = useSelector((state) => state.user?.authUser)
	const currentOrg = useSelector((state) => state.user?.currentOrg)
	const [blast, setBlast] = useState(null)
	const [stats, setStats] = useState(null)
	const [recipients, setRecipients] = useState([])
	const [recipientPage, setRecipientPage] = useState(1)
	const [recipientTotalCount, setRecipientTotalCount] = useState(0)
	const [statusFilter, setStatusFilter] = useState('')
	const [recipientSearch, setRecipientSearch] = useState('')
	const [loading, setLoading] = useState(true)
	const [loadError, setLoadError] = useState('')
	const [loadingRecipients, setLoadingRecipients] = useState(false)
	const [downloadingRecipients, setDownloadingRecipients] = useState(false)
	const [downloadError, setDownloadError] = useState('')
	const [queueHealth, setQueueHealth] = useState(null)
	const [draftRecipients, setDraftRecipients] = useState([])
	const [loadingDraftRecipients, setLoadingDraftRecipients] = useState(false)
	const [messageModalOpened, setMessageModalOpened] = useState(false)
	const [messagePreviewChannel, setMessagePreviewChannel] = useState('EMAIL')
	const [actionLoading, setActionLoading] = useState(false)
	const [actionError, setActionError] = useState('')
	const [sendModalMode, setSendModalMode] = useState(null)
	const [scheduleLocalValue, setScheduleLocalValue] = useState('')
	const [scheduleError, setScheduleError] = useState('')
	const [deleteDraftModalOpened, setDeleteDraftModalOpened] = useState(false)
	const [cancelScheduledModalOpened, setCancelScheduledModalOpened] =
		useState(false)
	const scheduledTimezone = experience?.timezone || getBrowserTimezone()

	const refreshBlastDetail = useCallback(async () => {
		const [blastResult, statsResult, queueResult] = await Promise.allSettled([
			getBlast(orgSlug, experienceSlug, id),
			getBlastStats(orgSlug, experienceSlug, id),
			getBlastQueueHealth(orgSlug, experienceSlug, id),
		])

		if (blastResult.status === 'fulfilled') {
			setBlast(blastResult.value)
			setLoadError('')
		} else {
			console.error('Error refreshing blast:', blastResult.reason)
			setLoadError(
				blastResult.reason?.message || 'Failed to load blast details.',
			)
		}

		if (statsResult.status === 'fulfilled') {
			setStats(statsResult.value)
		} else {
			console.error('Error refreshing blast stats:', statsResult.reason)
		}

		if (queueResult.status === 'fulfilled') {
			setQueueHealth(queueResult.value)
		} else {
			console.error('Error refreshing queue health:', queueResult.reason)
		}
	}, [orgSlug, experienceSlug, id])
	const isSendInFlight =
		blast?.status === 'QUEUED' || blast?.status === 'SENDING'
	const isQueuePreparationInProgress =
		isSendInFlight && toSafeCount(blast?.totalRecipients) === 0

	// ========================
	// Load Blast + Stats
	// ========================
	useEffect(() => {
		;(async () => {
			try {
				await refreshBlastDetail()
			} catch (err) {
				console.error('Error loading blast:', err)
			} finally {
				setLoading(false)
			}
		})()
	}, [refreshBlastDetail])

	// ========================
	// Load Recipients
	// ========================
	const loadRecipients = useCallback(
		async ({ page = 1, reset = false } = {}) => {
			setLoadingRecipients(true)
			try {
				const normalizedPage =
					Number.isFinite(Number(page)) && Number(page) > 0
						? Math.floor(Number(page))
						: 1
				const { recipients: rows, totalCount } = await listRecipients(
					orgSlug,
					experienceSlug,
					id,
					{
						status: statusFilter || undefined,
						search: recipientSearch || undefined,
						page: reset ? 1 : normalizedPage,
						take: RECIPIENTS_PAGE_SIZE,
					},
				)
				setRecipients(rows)
				setRecipientPage(normalizedPage)
				setRecipientTotalCount(toSafeCount(totalCount))
			} catch (err) {
				console.error('Error loading recipients:', err)
			} finally {
				setLoadingRecipients(false)
			}
		},
		[orgSlug, experienceSlug, id, statusFilter, recipientSearch],
	)

	useEffect(() => {
		if (blast?.status === 'DRAFT' || isQueuePreparationInProgress) return
		const debounceMs = recipientSearch.trim() ? 250 : 0
		const timeoutId = setTimeout(() => {
			loadRecipients({ page: 1, reset: true })
		}, debounceMs)
		return () => clearTimeout(timeoutId)
	}, [
		statusFilter,
		recipientSearch,
		loadRecipients,
		blast?.status,
		isQueuePreparationInProgress,
	]) // reload on filter/search change

	useEffect(() => {
		if (!isQueuePreparationInProgress) return
		setRecipients([])
		setRecipientPage(1)
		setRecipientTotalCount(0)
	}, [isQueuePreparationInProgress])

	useEffect(() => {
		if (!isSendInFlight) return

		let isUnmounted = false
		const refreshIfVisible = () => {
			if (isUnmounted) return
			if (
				typeof document !== 'undefined' &&
				document.visibilityState !== 'visible'
			) {
				return
			}
			refreshBlastDetail().catch((err) => {
				console.error('Error polling blast progress:', err)
			})
		}

		refreshIfVisible()
		const intervalId = setInterval(
			refreshIfVisible,
			BLAST_IN_FLIGHT_POLL_INTERVAL_MS,
		)
		const handleVisibilityChange = () => {
			if (
				typeof document !== 'undefined' &&
				document.visibilityState === 'visible'
			) {
				refreshIfVisible()
			}
		}
		if (typeof document !== 'undefined') {
			document.addEventListener('visibilitychange', handleVisibilityChange)
		}

		return () => {
			isUnmounted = true
			clearInterval(intervalId)
			if (typeof document !== 'undefined') {
				document.removeEventListener('visibilitychange', handleVisibilityChange)
			}
		}
	}, [isSendInFlight, refreshBlastDetail])

	useEffect(() => {
		const loadDraftRecipients = async () => {
			if (blast?.status !== 'DRAFT') {
				setDraftRecipients([])
				return
			}

			const selectedCheckInIds = Array.isArray(
				blast?.audienceFilter?.selectedCheckInIds,
			)
				? blast.audienceFilter.selectedCheckInIds
				: []

			if (!selectedCheckInIds.length) {
				setDraftRecipients([])
				return
			}

			setLoadingDraftRecipients(true)
			try {
				const allCheckIns = await getExperienceCheckIns(experienceSlug, orgSlug)
				const byId = new Map(
					(Array.isArray(allCheckIns) ? allCheckIns : []).map((row) => [
						row.id,
						row,
					]),
				)
				const nextRecipients = []
				const includeEmail =
					blast?.channel === 'EMAIL' || blast?.channel === 'BOTH'
				const includeSms = blast?.channel === 'SMS' || blast?.channel === 'BOTH'

				for (const checkInId of selectedCheckInIds) {
					const checkIn = byId.get(checkInId)
					if (!checkIn) continue

					if (includeEmail && checkIn?.travelerEmail) {
						nextRecipients.push({
							id: `${checkInId}:EMAIL`,
							travelerName: checkIn?.travelerName || '',
							address: checkIn.travelerEmail,
							channel: 'EMAIL',
							status: 'DRAFT_SELECTED',
							sentAt: null,
							deliveredAt: null,
							errorMessage: null,
						})
					}

					if (includeSms && checkIn?.travelerPhone) {
						nextRecipients.push({
							id: `${checkInId}:SMS`,
							travelerName: checkIn?.travelerName || '',
							address: checkIn.travelerPhone,
							channel: 'SMS',
							status: 'DRAFT_SELECTED',
							sentAt: null,
							deliveredAt: null,
							errorMessage: null,
						})
					}
				}

				setDraftRecipients(nextRecipients)
			} catch (err) {
				console.error('Error loading draft recipients:', err)
				setDraftRecipients([])
			} finally {
				setLoadingDraftRecipients(false)
			}
		}

		loadDraftRecipients()
	}, [
		blast?.id,
		blast?.status,
		blast?.channel,
		blast?.audienceFilter?.selectedCheckInIds,
		experienceSlug,
		orgSlug,
	])

	useEffect(() => {
		if (blast?.channel === 'SMS') {
			setMessagePreviewChannel('SMS')
			return
		}
		setMessagePreviewChannel('EMAIL')
	}, [blast?.id, blast?.channel])

	useEffect(() => {
		const scheduledAt = blast?.scheduledAt
		const scheduledAtMs = scheduledAt ? new Date(scheduledAt).getTime() : null
		const isFutureScheduled =
			Number.isFinite(scheduledAtMs) && scheduledAtMs > Date.now()
		setScheduleLocalValue(
			isFutureScheduled
				? toDateTimeLocalInTimezone(scheduledAt, scheduledTimezone)
				: '',
		)
	}, [blast?.id, blast?.scheduledAt, scheduledTimezone])

	if (loading) return <Loader mt='xl' />

	if (!blast) {
		return (
			<Stack align='center' mt='xl'>
				<Title order={3}>
					{loadError ? 'Messaging unavailable' : 'Blast not found'}
				</Title>
				{loadError && (
					<Text c='red' align='center'>
						{loadError}
					</Text>
				)}
				<Button
					component={Link}
					to={`/${orgSlug}/experiences/${experienceSlug}/messaging`}
					variant='default'
				>
					Back to Messaging
				</Button>
			</Stack>
		)
	}

	// ========================
	// Blast Actions
	// ========================
	const queueHealthColor =
		queueHealth?.level === 'error'
			? 'red'
			: queueHealth?.level === 'warn'
				? 'yellow'
				: 'gray'
	const resolvedExperienceName =
		getNonEmptyTrimmedValue(
			experience?.shortName,
			currentExperience?.shortName,
			experience?.name,
			currentExperience?.name,
			experienceSlug,
		) || 'Experience'
	const useExperiencePage =
		experience?.useExperiencePage ??
		currentExperience?.useExperiencePage ??
		true
	const previewExperienceId =
		experience?.id || currentExperience?.id || blast?.experienceId || ''
	const passPreviewUrl = buildPassPreviewUrl(
		useExperiencePage,
		previewExperienceId,
	)
	const renderedSubject = (() => {
		const subject = blast?.subject
		if (!subject) return 'Untitled Blast'
		return applyMessageVariables({
			value: subject,
			travelerName: user?.name || 'John Doe',
			groupLeaderName: 'Jane Doe',
			groupLeaderPhone: user?.phone || '+1 (555) 123-4567',
			groupMemberNames: 'Jeff Gordon, Richard Petty, Chase Elliott',
			experienceName: resolvedExperienceName,
			orgName: currentOrg?.orgName || 'LiveHQ',
			clickHereToViewPass: passPreviewUrl,
		})
	})()
	const scheduledAtMs = blast?.scheduledAt
		? new Date(blast.scheduledAt).getTime()
		: null
	const isFutureScheduled =
		Number.isFinite(scheduledAtMs) && scheduledAtMs > Date.now()
	const scheduledForLabel = isFutureScheduled
		? new Date(blast.scheduledAt).toLocaleString()
		: ''
	const canEditBlast =
		blast?.status === 'DRAFT' ||
		(blast?.status === 'QUEUED' && isFutureScheduled)
	const selectedCheckInIds = blast?.audienceFilter?.selectedCheckInIds
	const selectedTravelerCount =
		Array.isArray(selectedCheckInIds) && selectedCheckInIds.length > 0
			? new Set(selectedCheckInIds).size
			: toSafeCount(blast?.audienceFilter?.counts?.travelers)
	const baseRecipients =
		blast?.status === 'DRAFT'
			? recipients.length > 0
				? recipients
				: draftRecipients
			: recipients
	const normalizedRecipientSearch = String(recipientSearch || '')
		.trim()
		.toLowerCase()
	const displayedRecipients =
		blast?.status !== 'DRAFT' || !normalizedRecipientSearch
			? baseRecipients
			: baseRecipients.filter((recipient) => {
					const name = String(recipient?.travelerName || '').toLowerCase()
					const address = String(recipient?.address || '').toLowerCase()
					return (
						name.includes(normalizedRecipientSearch) ||
						address.includes(normalizedRecipientSearch)
					)
				})
	const totalRecipientsLabel =
		blast?.status === 'DRAFT'
			? baseRecipients.length
			: isQueuePreparationInProgress
				? 0
				: (stats?.rollups.totalRecipients ?? 0)
	const totalRecipientsForProgress = toSafeCount(totalRecipientsLabel)
	const queuedRecipientsRaw =
		blast?.status === 'DRAFT'
			? 0
			: toSafeCount(
					stats?.live?.QUEUED ?? queueHealth?.blast?.queuedRecipients ?? 0,
				)
	const queuedRecipientsNow =
		totalRecipientsForProgress > 0
			? Math.min(queuedRecipientsRaw, totalRecipientsForProgress)
			: queuedRecipientsRaw
	const processedRecipientsNow =
		totalRecipientsForProgress > 0
			? Math.max(0, totalRecipientsForProgress - queuedRecipientsNow)
			: 0
	const processedProgressValue =
		totalRecipientsForProgress > 0
			? (processedRecipientsNow / totalRecipientsForProgress) * 100
			: 0
	const filteredRecipientsTotal =
		blast?.status === 'DRAFT' ? displayedRecipients.length : recipientTotalCount
	const recipientTotalPages = Math.ceil(
		filteredRecipientsTotal / RECIPIENTS_PAGE_SIZE,
	)
	const recipientCurrentStart =
		filteredRecipientsTotal > 0
			? (recipientPage - 1) * RECIPIENTS_PAGE_SIZE + 1
			: 0
	const recipientCurrentEnd =
		filteredRecipientsTotal > 0
			? Math.min(
					(recipientPage - 1) * RECIPIENTS_PAGE_SIZE +
						displayedRecipients.length,
					filteredRecipientsTotal,
				)
			: 0
	const recipientCountLabel =
		filteredRecipientsTotal > 0
			? `Showing ${recipientCurrentStart}-${recipientCurrentEnd} of ${filteredRecipientsTotal} recipients`
			: 'Showing 0 recipients'
	const statusPillCounts = {
		SENT: stats?.rollups.sentCount ?? 0,
		DELIVERED: stats?.rollups.deliveredCount ?? 0,
		OPENED: stats?.rollups.openedCount ?? 0,
		// CLICKED: stats?.rollups.clickedCount ?? 0,
		BOUNCED: stats?.rollups.bouncedCount ?? 0,
		UNSUBSCRIBED: stats?.rollups.unsubCount ?? 0,
		FAILED: stats?.rollups.failedCount ?? 0,
	}
	const statusPillsDisabled =
		blast?.status === 'DRAFT' || isQueuePreparationInProgress
	const handleStatusPillClick = (nextStatus = '') => {
		if (statusPillsDisabled) return
		setStatusFilter((current) => (current === nextStatus ? '' : nextStatus))
	}

	const refreshAfterAction = async () => {
		await Promise.all([
			refreshBlastDetail(),
			blast?.status === 'DRAFT'
				? Promise.resolve()
				: loadRecipients({ page: 1, reset: true }),
		])
	}

	const handleRecipientPageChange = async (nextPage) => {
		if (
			loadingRecipients ||
			blast?.status === 'DRAFT' ||
			isQueuePreparationInProgress
		) {
			return
		}
		if (!Number.isFinite(Number(nextPage))) return
		const normalizedPage = Math.floor(Number(nextPage))
		if (normalizedPage < 1 || normalizedPage === recipientPage) return
		await loadRecipients({ page: normalizedPage })
	}

	const handleDownloadRecipients = async () => {
		setDownloadingRecipients(true)
		setDownloadError('')
		try {
			const rows = await listAllRecipients(orgSlug, experienceSlug, id, {
				status: statusFilter || undefined,
				search: recipientSearch || undefined,
			})
			const csv = buildBlastRecipientCsv(rows)
			saveAs(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' }),
				`${experienceSlug}_blast_${id}_recipients.csv`)
		} catch (err) {
			setDownloadError(err.message || 'Failed to download recipients.')
		} finally {
			setDownloadingRecipients(false)
		}
	}

	const handleSendNow = async () => {
		if (actionLoading) return
		setActionLoading(true)
		setActionError('')
		try {
			await sendBlast(orgSlug, experienceSlug, id)
			await refreshAfterAction()
		} catch (err) {
			setActionError(err.message || 'Failed to send blast.')
		} finally {
			setActionLoading(false)
			setSendModalMode(null)
		}
	}

	const handleConfirmScheduleSend = async () => {
		if (actionLoading) return
		const scheduledAt = toUtcIsoInTimezone(
			scheduleLocalValue,
			scheduledTimezone,
		)
		const validationError = getBlastScheduleError(scheduledAt)
		setScheduleError(validationError)
		if (validationError) return
		setActionLoading(true)
		setActionError('')
		try {
			await sendBlast(orgSlug, experienceSlug, id, {
				scheduledAt,
				scheduledTimezone,
			})
			setSendModalMode(null)
			await refreshAfterAction()
		} catch (err) {
			setActionError(err.message || 'Failed to schedule blast.')
		} finally {
			setActionLoading(false)
		}
	}

	const handleDeleteDraft = async () => {
		setActionLoading(true)
		setActionError('')
		try {
			await deleteBlast(orgSlug, experienceSlug, id)
			navigate(`/${orgSlug}/experiences/${experienceSlug}/messaging`)
		} catch (err) {
			setActionError(err.message || 'Failed to delete draft.')
		} finally {
			setActionLoading(false)
			setDeleteDraftModalOpened(false)
		}
	}

	const handleCancelScheduled = async (action) => {
		setActionLoading(true)
		setActionError('')
		try {
			await cancelScheduledBlast(orgSlug, experienceSlug, id, { action })
			setCancelScheduledModalOpened(false)
			if (action === 'delete') {
				navigate(`/${orgSlug}/experiences/${experienceSlug}/messaging`)
				return
			}
			await refreshAfterAction()
		} catch (err) {
			setActionError(err.message || 'Failed to cancel scheduled blast.')
		} finally {
			setActionLoading(false)
		}
	}

	const blastContent =
		blast?.audienceFilter &&
		typeof blast.audienceFilter === 'object' &&
		blast.audienceFilter.content &&
		typeof blast.audienceFilter.content === 'object'
			? blast.audienceFilter.content
			: {}
	const hasEmailChannel =
		blast?.channel === 'EMAIL' || blast?.channel === 'BOTH'
	const hasSmsChannel = blast?.channel === 'SMS' || blast?.channel === 'BOTH'
	const selectedPreviewChannel =
		blast?.channel === 'BOTH'
			? messagePreviewChannel
			: hasSmsChannel
				? 'SMS'
				: 'EMAIL'
	const rawEmailSubject =
		typeof blastContent.emailSubject === 'string'
			? blastContent.emailSubject
			: blast?.subject || ''
	const rawEmailBody =
		typeof blastContent.emailBodyText === 'string'
			? blastContent.emailBodyText
			: hasEmailChannel
				? blast?.bodyText || ''
				: ''
	const rawSmsBody =
		typeof blastContent.smsBodyText === 'string'
			? blastContent.smsBodyText
			: hasSmsChannel
				? blast?.bodyText || ''
				: ''
	const rawSmsMediaUrl =
		typeof blastContent.smsMediaUrl === 'string'
			? blastContent.smsMediaUrl
			: typeof blastContent.mmsMediaUrl === 'string'
				? blastContent.mmsMediaUrl
				: ''
	const blastTemplate = getStockTemplate(blast?.audienceFilter?.templateKey)
	const shouldAppendPassLinkToSms =
		isCheckInPassTemplate(blastTemplate) &&
		blastContent.appendPassLinkToSms !== false
	const previewVariables = {
		travelerName: user?.name || 'John Doe',
		groupLeaderName: 'Jane Doe',
		groupLeaderPhone: user?.phone || '+1 (555) 123-4567',
		groupMemberNames: 'Jeff Gordon, Richard Petty, Chase Elliott',
		experienceName: resolvedExperienceName,
		orgName: currentOrg?.orgName || 'LiveHQ',
		clickHereToViewPass: passPreviewUrl,
	}
	const renderedEmailSubject = (() => {
		return applyMessageVariables({
			value: rawEmailSubject,
			...previewVariables,
		})
	})()
	const renderedEmailBody = (() => {
		return applyMessageVariables({
			value: rawEmailBody,
			...previewVariables,
		})
	})()
	const renderedSmsBody = (() => {
		const body = applyMessageVariables({
			value: rawSmsBody,
			...previewVariables,
		})
		if (!shouldAppendPassLinkToSms) return body
		return appendPassLinkToSmsBody(
			body,
			previewVariables.clickHereToViewPass,
		)
	})()

	// ========================
	// Render
	// ========================
	return (
		<Stack spacing='md'>
			<Button
				component={Link}
				to={`/${orgSlug}/experiences/${experienceSlug}/messaging`}
				variant='subtle'
				size='xs'
				style={{ alignSelf: 'flex-start' }}
			>
				Back to Blasts
			</Button>

			{/* ===== Header ===== */}
			<Group position='apart'>
				<Group>
					<Title order={2}>{renderedSubject}</Title>
					<Badge>{blast?.channel}</Badge>
					<Badge color='gray'>{blast?.status}</Badge>
					{isFutureScheduled && (
						<Badge color='blue'>{`Scheduled for ${scheduledForLabel}`}</Badge>
					)}
					{queueHealth && (
						<Tooltip
							withArrow
							multiline
							width={320}
							position='right'
							label={
								<Stack spacing={4}>
									<Text size='xs' fw={600}>
										Queue Health: {queueHealth.summary}
									</Text>
									<Text size='xs'>
										Workers - SMS: {queueHealth?.queue?.smsWorkers ?? '—'} |
										Email: {queueHealth?.queue?.emailWorkers ?? '—'}
									</Text>
									<Text size='xs'>
										Blast Queue - {queueHealth?.blast?.queuedRecipients ?? 0}/
										{queueHealth?.blast?.totalRecipients ?? 0} queued
									</Text>
									{queueHealth?.issues?.length > 0 && (
										<Text size='xs'>
											{queueHealth.issues
												.map((issue) => issue.message)
												.join(' | ')}
										</Text>
									)}
								</Stack>
							}
						>
							<ActionIcon variant='subtle' color={queueHealthColor} size='sm'>
								<AlertCircle size={15} />
							</ActionIcon>
						</Tooltip>
					)}
				</Group>
				<Group spacing='xs'>
					{canEditBlast && (
						<Button
							variant='default'
							component={Link}
							to={`/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${id}/edit`}
							disabled={actionLoading}
						>
							Edit Message & Recipients
						</Button>
					)}
					{blast?.status === 'DRAFT' && (
						<>
							<Menu withinPortal position='bottom-end'>
								<Menu.Target>
									<Button
										rightSection='▾'
										loading={actionLoading}
										disabled={actionLoading}
									>
										Send Blast
									</Button>
								</Menu.Target>
								<Menu.Dropdown>
									<Menu.Item
										onClick={() => setSendModalMode('now')}
										disabled={actionLoading}
									>
										Send Now
									</Menu.Item>
									<Menu.Item
										onClick={() => setSendModalMode('schedule')}
										disabled={actionLoading}
									>
										Send Later
									</Menu.Item>
								</Menu.Dropdown>
							</Menu>
							<Button
								color='red'
								variant='light'
								onClick={() => setDeleteDraftModalOpened(true)}
								disabled={actionLoading}
							>
								Delete Draft
							</Button>
						</>
					)}
					{blast?.status === 'QUEUED' && isFutureScheduled && (
						<Button
							color='red'
							variant='light'
							onClick={() => setCancelScheduledModalOpened(true)}
							disabled={actionLoading}
						>
							Cancel Scheduled Blast
						</Button>
					)}
				</Group>
			</Group>

			<Text size='sm' color='dimmed'>
				Created: {new Date(blast?.createdAt).toLocaleString()}
			</Text>
			{actionError && <Text c='red'>{actionError}</Text>}

			<Divider my='xs' />

			{/* ===== Summary Card ===== */}
			<Card withBorder>
				<Text fw={500}>Delivery Summary</Text>
				{isQueuePreparationInProgress ? (
					<Stack mt='sm' spacing='xs'>
						<Group spacing='xs'>
							<Loader size='sm' />
							<Text size='sm'>Preparing recipients...</Text>
						</Group>
						<Text size='sm' color='dimmed'>
							Queueing is running in the background. Counts appear after all
							recipients are queued.
						</Text>
					</Stack>
				) : (
					blast?.status !== 'DRAFT' && (
						<Stack mt='sm' spacing='xs'>
							<Group position='apart' spacing='xs'>
								<Text size='sm'>Processed</Text>
								<Text size='sm' color='dimmed'>
									{processedRecipientsNow.toLocaleString()} /{' '}
									{totalRecipientsForProgress.toLocaleString()}
								</Text>
							</Group>
							<Progress
								value={processedProgressValue}
								color='teal'
								radius='xl'
							/>
						</Stack>
					)
				)}

				{isQueuePreparationInProgress ? (
					<Group mt='sm' spacing='xs'>
						<Badge color='gray'>Queueing recipients...</Badge>
					</Group>
				) : (
					<Group mt='sm' spacing='xs'>
						<Badge
							variant={statusFilter ? 'outline' : 'filled'}
							color='gray'
							onClick={() => handleStatusPillClick('')}
							style={{
								cursor: statusPillsDisabled ? 'default' : 'pointer',
								opacity: statusPillsDisabled ? 0.7 : 1,
							}}
						>
							total {totalRecipientsLabel}
						</Badge>
						{RECIPIENT_STATUS_PILLS.map((pill) => (
							<Badge
								key={pill.value}
								color={pill.color}
								variant={statusFilter === pill.value ? 'filled' : 'light'}
								onClick={() => handleStatusPillClick(pill.value)}
								style={{
									cursor: statusPillsDisabled ? 'default' : 'pointer',
									opacity: statusPillsDisabled ? 0.7 : 1,
								}}
							>
								{pill.label} {statusPillCounts[pill.value] ?? 0}
							</Badge>
						))}
					</Group>
				)}
			</Card>

			{/* ===== Filters ===== */}
			<Group>
				<TextInput
					placeholder='Search traveler name or address'
					value={recipientSearch}
					onChange={(event) => setRecipientSearch(event.currentTarget.value)}
					disabled={isQueuePreparationInProgress}
					w={320}
				/>
				<Button
					onClick={async () => {
						await Promise.all([
							blast?.status === 'DRAFT' || isQueuePreparationInProgress
								? Promise.resolve()
								: loadRecipients({ page: recipientPage }),
							refreshBlastDetail(),
						])
					}}
					variant='default'
				>
					Refresh
				</Button>
				<Button variant='default' onClick={() => setMessageModalOpened(true)}>
					View Message
				</Button>
			</Group>

			{/* ===== Recipients Table ===== */}
			<Table striped highlightOnHover>
				<thead>
					<tr>
						<th>Traveler</th>
						<th>Address</th>
						<th>Channel</th>
						<th>Status</th>
						<th>Sent</th>
						<th>Delivered</th>
						<th>Opened</th>
					</tr>
				</thead>
				<tbody>
					{isQueuePreparationInProgress ? (
						<tr>
							<td colSpan={7}>
								<Stack align='center' spacing='xs'>
									<Loader size='sm' />
									<Text align='center' color='dimmed'>
										Preparing recipient list. This can take a bit for large
										blasts.
									</Text>
								</Stack>
							</td>
						</tr>
					) : loadingDraftRecipients ? (
						<tr>
							<td colSpan={7}>
								<Text align='center' color='dimmed'>
									Loading draft recipients...
								</Text>
							</td>
						</tr>
					) : displayedRecipients.length === 0 ? (
						<tr>
							<td colSpan={7}>
								<Text align='center' color='dimmed'>
									{recipientSearch.trim()
										? 'No recipients match your search.'
										: blast?.status === 'DRAFT'
											? 'No recipients selected for this draft.'
											: 'No recipients yet.'}
								</Text>
							</td>
						</tr>
					) : (
						displayedRecipients.map((r) => (
							<tr key={r.id}>
								<td>{r.travelerName || '—'}</td>
								<td>{r.address}</td>
								<td>{r.channel}</td>
								<td>{r.status}</td>
								<td>{r.sentAt ? new Date(r.sentAt).toLocaleString() : '—'}</td>
								<td>
									{r.deliveredAt
										? new Date(r.deliveredAt).toLocaleString()
										: '—'}
								</td>
								<td>
									{r.openedAt ? new Date(r.openedAt).toLocaleString() : '—'}
								</td>
							</tr>
						))
					)}
				</tbody>
			</Table>

			{blast?.status !== 'DRAFT' && !isQueuePreparationInProgress && (
				<Box mt='sm'>
					<Group spacing='sm' mb={recipientTotalPages > 1 ? 'xs' : 0}>
						<Text size='sm' c='dimmed'>{recipientCountLabel}</Text>
						<Anchor
							component='button'
							type='button'
							color='blue'
							size='sm'
							onClick={handleDownloadRecipients}
							disabled={downloadingRecipients || loadingRecipients || filteredRecipientsTotal === 0}
							style={{ border: 0, padding: 0, background: 'none' }}
							title='Download all recipients matching the current filters'
						>
							{downloadingRecipients ? 'Downloading...' : 'Download'}
						</Anchor>
					</Group>
					{downloadError && <Text size='sm' c='red' role='alert'>{downloadError}</Text>}
					{recipientTotalPages > 1 && (
						<Pagination
							value={recipientPage}
							onChange={handleRecipientPageChange}
							total={recipientTotalPages}
							size='sm'
							disabled={loadingRecipients}
						/>
					)}
				</Box>
			)}

			<BlastSendModal
				mode={sendModalMode}
				onClose={() => setSendModalMode(null)}
				onConfirm={sendModalMode === 'now' ? handleSendNow : handleConfirmScheduleSend}
				travelerCount={selectedTravelerCount}
				submitting={actionLoading}
				scheduledTimezone={scheduledTimezone}
				scheduleLocalValue={scheduleLocalValue}
				scheduleError={scheduleError}
				onScheduleChange={(value) => {
					setScheduleLocalValue(value)
					setScheduleError('')
				}}
			/>

			<Modal
				opened={deleteDraftModalOpened}
				onClose={() => setDeleteDraftModalOpened(false)}
				title='Delete Draft'
				centered
			>
				<Stack spacing='sm'>
					<Text>
						Delete this draft blast? This removes the draft permanently.
					</Text>
					<Group>
						<Button
							variant='default'
							onClick={() => setDeleteDraftModalOpened(false)}
							disabled={actionLoading}
						>
							Keep Draft
						</Button>
						<Button
							color='red'
							onClick={handleDeleteDraft}
							loading={actionLoading}
						>
							Delete Draft
						</Button>
					</Group>
				</Stack>
			</Modal>

			<Modal
				opened={cancelScheduledModalOpened}
				onClose={() => setCancelScheduledModalOpened(false)}
				title='Cancel Scheduled Blast'
				centered
			>
				<Stack spacing='sm'>
					<Text>
						Choose what should happen after canceling this scheduled blast.
					</Text>
					<Group>
						<Button
							variant='default'
							onClick={() => setCancelScheduledModalOpened(false)}
							disabled={actionLoading}
						>
							Keep Scheduled
						</Button>
						<Button
							variant='light'
							onClick={() => handleCancelScheduled('draft')}
							loading={actionLoading}
						>
							Cancel & Save as Draft
						</Button>
						<Button
							color='red'
							onClick={() => handleCancelScheduled('delete')}
							loading={actionLoading}
						>
							Cancel & Delete
						</Button>
					</Group>
				</Stack>
			</Modal>

			<Modal
				opened={messageModalOpened}
				onClose={() => setMessageModalOpened(false)}
				title='Message Preview'
				size='lg'
			>
				<Stack spacing='md'>
					{blast?.channel === 'BOTH' && (
						<SegmentedControl
							value={selectedPreviewChannel}
							onChange={setMessagePreviewChannel}
							data={[
								{ label: 'Email', value: 'EMAIL' },
								{ label: 'SMS', value: 'SMS' },
							]}
						/>
					)}

					{selectedPreviewChannel === 'EMAIL' ? (
						<>
							<Text size='sm' fw={600}>
								Subject
							</Text>
							<Card withBorder p='sm'>
								<Text style={{ whiteSpace: 'pre-wrap' }}>
									{renderedEmailSubject || '—'}
								</Text>
							</Card>
							<Text size='sm' fw={600}>
								Body
							</Text>
							<Card withBorder p='sm'>
								<Text style={{ whiteSpace: 'pre-wrap' }}>
									{renderedEmailBody || 'No email message content available.'}
								</Text>
							</Card>
						</>
					) : (
						<>
							<Text size='sm' fw={600}>
								SMS Message
							</Text>
							<Card withBorder p='sm'>
								<Text style={{ whiteSpace: 'pre-wrap' }}>
									{renderedSmsBody || 'No SMS message content available.'}
								</Text>
								{rawSmsMediaUrl && (
									<Box mt='sm'>
										<img
											src={rawSmsMediaUrl}
											alt='MMS preview'
											style={{
												width: '100%',
												maxWidth: 360,
												borderRadius: 8,
												display: 'block',
											}}
										/>
									</Box>
								)}
							</Card>
						</>
					)}
				</Stack>
			</Modal>
		</Stack>
	)
}
